<template>
  <div class="dashboard">
    <b-loading :active="loading" />
    <div class="columns">
      <div class="column is-half-desktop is-full-mobile">
        <div class="box status">
          <div class="status__description">
            <h2 class="status__description--title">你好！</h2>
            <p v-if="isHead" class="status__description--status">
              歡迎使用好彙訂，來看看大家訂購的進度吧！
            </p>
            <p v-else class="status__description--status">
              <template v-if="info.unverified > 0">
                目前尚有 {{ info.unverified }} 名人員尚未接受邀請唷！
                <br>
                前往 <router-link to="/member/list">人員管理</router-link> 重發驗證信
              </template>
              <template v-else>
                來約團訂購便當、飲料或是日用品吧！
              </template>
            </p>
          </div>
          <div class="status__info">
            <div class="status__info--wrapper">
              <div class="card">
                <p class="card__title">開放訂購中</p>
                <p class="card__value">{{ info.in_progress }}</p>
              </div>
              <div class="card">
                <p class="card__title">已公告</p>
                <p class="card__value">{{ info.scheduled }}</p>
              </div>
            </div>
            <router-link
              to="/order/create"
              class="button is-primary is-fullwidth"
              :class="{ 'is-outlined': orderPreview.length }">
              發起新訂購
            </router-link>
          </div>
        </div>
        <div class="box preview">
          <div v-if="orderPreview.length" class="box__title">
            即將結束訂購
            <router-link
              to="/order/list"
              class="box__title--action">
              查看更多
            </router-link>
          </div>
          <div class="box__content">
            <div class="preview--empty" v-if="!orderPreview.length">
              <img src="@/assets/images/dashboard-placeholder.svg" alt="尚無開放訂購中的訂購">
            </div>
            <div v-for="order in orderPreview" class="card" :key="`order-${order.id}`">
              <b-field horizontal label="訂購名稱">{{ order.name }}</b-field>
              <b-field horizontal label="訂購時間">
                {{ humanizeDate(order.start_time) }} - {{ humanizeDate(order.end_time) }}
              </b-field>
              <b-field horizontal label="訂購人數進度">
                <div class="order-progress">
                  <div class="is-flex mb-2">
                    <div class="is-align-self-center">
                      <h2>已訂人數</h2>
                      <h3 class="order-progress__value has-text-link">
                        {{ order.ordered_member_count || 0 }}
                      </h3>
                    </div>
                    <progress
                      :max="order.expected_member_count || 0"
                      :value="order.ordered_member_count || 0"
                      class="progress is-align-self-flex-end is-link mx-2 mb-2"
                    />
                    <div class="is-align-self-center">
                      <h2>未訂人數</h2>
                      <h3 class="order-progress__value has-text-grey">
                        {{ order.unordered_member_count || 0 }}
                      </h3>
                    </div>
                  </div>

                  <div class="level is-justify-content-center">
                    <div>預計總人數</div>
                    <h3 class="ml-3">
                      {{ order.expected_member_count }}
                    </h3>
                  </div>
                </div>
              </b-field>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-half-desktop is-full-mobile">
        <div class="box calendar">
          <div class="box__title">行事曆</div>
          <div class="box__content">
            <div class="flex overflow-auto">
              <b-datepicker
                v-model="selectedDate"
                inline
                locale="zh-TW"
                :events="calendarEvents"
                @change-year="onYearChange"
                @change-month="onMonthChange" />
              <div v-if="selectedDate" class="flex-grow px-4">
                <h2 class="calendar__title">{{ humanizeDate(selectedDate) }}</h2>
                <div
                  v-if="!calendarEventInfo.length"
                  class="flex flex-col items-center justify-center"
                >
                  <p>沒有排定的訂購唷！</p>
                  <div class="has-text-centered mt-6">
                    <img src="@/assets/images/empty-calendar-event.svg" alt="當日沒有排定的訂購">
                  </div>
                </div>
                <div
                  v-for="(info, index) in calendarEventInfo"
                  class="calendar__event"
                  :key="`info-${info.name}-${index}`"
                >
                  <h3 class="calendar__event--title">{{ info.name }}</h3>
                  <p class="calendar__event--time">
                    {{ humanizeDateTime(info.start_time) }} -
                    {{ humanizeDateTime(info.end_time) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="box news">
          <div class="box__title">快速連結</div>
          <div class="box__content">
            <div class="card">
              <b-field
                custom-class="font-normal text-gray-500"
                horizontal
                label="點餐網址"
              >
                <div class="flex items-center">
                  <a
                    :href="loginLink"
                    :style="{'overflow-wrap': 'anywhere'}"
                    class="hover:text-blue-500 text-blue-400"
                    target="_blank"
                  >
                      {{ loginLink }}
                  </a>
                  <button
                    class="hover:text-black text-gray-500 ml-4"
                    v-clipboard:copy="loginLink"
                    v-clipboard:success="onCopySuccess"
                    v-clipboard:error="onCopyError"
                  >
                    <i class="icon-copy-2 text-2xl" />
                  </button>
                </div>
              </b-field>
              <b-field
                custom-class="font-normal text-gray-500"
                horizontal
                label="核銷網址"
              >
                <div class="flex items-center">
                  <a
                    :href="reimburseLink"
                    :style="{'overflow-wrap': 'anywhere'}"
                    class="hover:text-blue-500 text-blue-400"
                    target="_blank"
                  >
                      {{ reimburseLink }}
                  </a>
                  <button
                    class="hover:text-black text-gray-500 ml-4"
                    v-clipboard:copy="reimburseLink"
                    v-clipboard:success="onCopySuccess"
                    v-clipboard:error="onCopyError"
                  >
                    <i class="icon-copy-2 text-2xl" />
                  </button>
                </div>
              </b-field>
            </div>
          </div>
        </div>
        <div class="box news">
          <div class="box__title">最新消息</div>
          <div class="box__content">
            <div v-if="news.length" class="card">
              <a
                v-for="(item, newsIndex) in news"
                :alt="item.title"
                :href="item.link"
                :key="`news-${newsIndex}`"
                target="_blank"
                class="news__item"
              >
                <label
                  :title="item.title"
                  class="news__title"
                >
                  {{ item.title }}
                </label>
                <span class="ml-5 news__date">
                  {{ item.date }}
                </span>
              </a>
            </div>
            <div v-else>
              {{ newsPlaceholder }}
            </div>
          </div>
        </div>
        <div v-if="false" class="box tips">
          <div class="box__title">
            小教學
            <a
              href="#"
              class="box__title--action"
              @click.prevent="() => null">
              查看更多
            </a>
          </div>
          <div class="box__content">
            <div class="card">
              <ol>
                <li>從側邊欄進入店家管理後，點擊"新增店家"</li>
                <li>輸入店家名稱及訂購方式</li>
                <li>建立菜單</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dashboard from './Dashboard';

export default Dashboard;
</script>

<style lang="scss">
@import 'DashboardTutorial';
</style>

<style lang="scss" scoped>
@import 'Dashboard';
</style>
